export type FlowRect = { x: number; y: number; width: number; height: number }

/** Отрезок направляющей во флоу-координатах: `at` — по оси выравнивания, `from`–`to` — поперёк. */
export type FlowGuide = { orientation: "vertical" | "horizontal"; at: number; from: number; to: number }

type Axis = { start: (rect: FlowRect) => number; size: (rect: FlowRect) => number }

const X_AXIS: Axis = { start: (rect) => rect.x, size: (rect) => rect.width }
const Y_AXIS: Axis = { start: (rect) => rect.y, size: (rect) => rect.height }

/** Субпиксельные расхождения после зума не должны выкидывать ноду из отрезка. */
const SAME_ANCHOR_EPSILON = 0.5

function anchors(rect: FlowRect, axis: Axis): number[] {
  const start = axis.start(rect)
  const size = axis.size(rect)
  return [start, start + size]
}

function closestEdge(source: number, others: FlowRect[], threshold: number, axis: Axis) {
  let best: { delta: number; at: number } | null = null
  for (const other of others) {
    for (const target of anchors(other, axis)) {
      const delta = target - source
      if (Math.abs(delta) > threshold) continue
      if (!best || Math.abs(delta) < Math.abs(best.delta)) best = { delta, at: target }
    }
  }
  return best
}

function closestAnchor(dragged: FlowRect, others: FlowRect[], threshold: number, axis: Axis) {
  let best: { delta: number; at: number } | null = null
  for (const source of anchors(dragged, axis)) {
    const candidate = closestEdge(source, others, threshold, axis)
    if (!candidate) continue
    if (!best || Math.abs(candidate.delta) < Math.abs(best.delta)) best = candidate
  }
  return best
}

function guideSpan(at: number, dragged: FlowRect, others: FlowRect[], axis: Axis, cross: Axis) {
  const aligned = others.filter((rect) =>
    anchors(rect, axis).some((value) => Math.abs(value - at) <= SAME_ANCHOR_EPSILON),
  )
  const rects = [dragged, ...aligned]
  return {
    from: Math.min(...rects.map((rect) => cross.start(rect))),
    to: Math.max(...rects.map((rect) => cross.start(rect) + cross.size(rect))),
  }
}

/** Примагничивает перетаскиваемую ноду к краям соседей; по каждой оси — одна ближайшая линия. */
export function flowAlignmentGuides(
  dragged: FlowRect,
  others: FlowRect[],
  threshold: number,
): { x: number; y: number; guides: FlowGuide[] } {
  const vertical = closestAnchor(dragged, others, threshold, X_AXIS)
  const horizontal = closestAnchor(dragged, others, threshold, Y_AXIS)
  const snapped = { ...dragged, x: dragged.x + (vertical?.delta ?? 0), y: dragged.y + (horizontal?.delta ?? 0) }
  const guides: FlowGuide[] = []
  if (vertical) {
    guides.push({
      orientation: "vertical",
      at: vertical.at,
      ...guideSpan(vertical.at, snapped, others, X_AXIS, Y_AXIS),
    })
  }
  if (horizontal) {
    guides.push({
      orientation: "horizontal",
      at: horizontal.at,
      ...guideSpan(horizontal.at, snapped, others, Y_AXIS, X_AXIS),
    })
  }
  return { x: snapped.x, y: snapped.y, guides }
}

function unionRect(rects: FlowRect[]): FlowRect {
  let x = Infinity
  let y = Infinity
  let right = -Infinity
  let bottom = -Infinity
  for (const rect of rects) {
    x = Math.min(x, rect.x)
    y = Math.min(y, rect.y)
    right = Math.max(right, rect.x + rect.width)
    bottom = Math.max(bottom, rect.y + rect.height)
  }
  return { x, y, width: right - x, height: bottom - y }
}

/** Примагничивает рамку выделенной группы. Сдвиг один на все ноды, линия — по внешнему краю. */
export function flowGroupAlignmentGuides(
  dragged: FlowRect[],
  others: FlowRect[],
  threshold: number,
): { dx: number; dy: number; guides: FlowGuide[] } {
  if (dragged.length === 0) return { dx: 0, dy: 0, guides: [] }
  const bounds = unionRect(dragged)
  const snap = flowAlignmentGuides(bounds, others, threshold)
  return { dx: snap.x - bounds.x, dy: snap.y - bounds.y, guides: snap.guides }
}

/** Угол, который остаётся на месте: противоположный тянущейся ручке. */
export type FlowResizeAnchor = { x: "left" | "right"; y: "top" | "bottom" }

export type FlowResizeLimits = { minWidth: number; maxWidth: number }

type ResizeSnap = { rect: FlowRect; guides: FlowGuide[]; anchor: FlowResizeAnchor | null }

function fixedEdge(proposedStart: number, proposedEnd: number, previousStart: number, previousEnd: number) {
  const startFixed = Math.abs(proposedStart - previousStart) <= SAME_ANCHOR_EPSILON
  const endFixed = Math.abs(proposedEnd - previousEnd) <= SAME_ANCHOR_EPSILON
  if (startFixed && !endFixed) return "start" as const
  if (endFixed && !startFixed) return "end" as const
  return null
}

function resizeAnchor(proposed: FlowRect, previous: FlowRect): FlowResizeAnchor | null {
  const x = fixedEdge(proposed.x, proposed.x + proposed.width, previous.x, previous.x + previous.width)
  const y = fixedEdge(proposed.y, proposed.y + proposed.height, previous.y, previous.y + previous.height)
  if (!x || !y) return null
  return { x: x === "start" ? "left" : "right", y: y === "start" ? "top" : "bottom" }
}

function movingEdge(rect: FlowRect, anchor: FlowResizeAnchor, axis: "x" | "y") {
  if (axis === "x") return anchor.x === "left" ? rect.x + rect.width : rect.x
  return anchor.y === "top" ? rect.y + rect.height : rect.y
}

function resizedRect(proposed: FlowRect, anchor: FlowResizeAnchor, axis: "x" | "y", delta: number): FlowRect | null {
  const aspect = proposed.width / proposed.height
  if (!Number.isFinite(aspect) || aspect <= 0) return null
  let width = proposed.width
  let height = proposed.height
  if (axis === "x") {
    width = anchor.x === "left" ? proposed.width + delta : proposed.width - delta
    height = width / aspect
  } else {
    height = anchor.y === "top" ? proposed.height + delta : proposed.height - delta
    width = height * aspect
  }
  return {
    x: anchor.x === "left" ? proposed.x : proposed.x + proposed.width - width,
    y: anchor.y === "top" ? proposed.y : proposed.y + proposed.height - height,
    width,
    height,
  }
}

function resizeFits(rect: FlowRect, limits?: FlowResizeLimits) {
  if (!(rect.width > 0) || !(rect.height > 0)) return false
  if (!Number.isFinite(rect.x) || !Number.isFinite(rect.y)) return false
  if (!limits) return true
  return rect.width >= limits.minWidth && rect.width <= limits.maxWidth
}

function resizeGuide(axis: "x" | "y", at: number, snapped: FlowRect, others: FlowRect[]): FlowGuide {
  if (axis === "x") {
    return { orientation: "vertical", at, ...guideSpan(at, snapped, others, X_AXIS, Y_AXIS) }
  }
  return { orientation: "horizontal", at, ...guideSpan(at, snapped, others, Y_AXIS, X_AXIS) }
}

/**
 * Примагничивает свободный край ресайза к краю соседа.
 * Вторая сторона следует за пропорцией предложенного кадра, якорь не сдвигается.
 * Ближайший кандидат, который вылезает за `limits`, пропускается.
 */
export function flowResizeSnap(
  proposed: FlowRect,
  previous: FlowRect,
  others: FlowRect[],
  threshold: number,
  limits?: FlowResizeLimits,
  lockedAnchor?: FlowResizeAnchor | null,
): ResizeSnap {
  const anchor = lockedAnchor ?? resizeAnchor(proposed, previous)
  if (!anchor || !(proposed.width > 0) || !(proposed.height > 0)) {
    return { rect: proposed, guides: [], anchor }
  }

  const alongX = closestEdge(movingEdge(proposed, anchor, "x"), others, threshold, X_AXIS)
  const alongY = closestEdge(movingEdge(proposed, anchor, "y"), others, threshold, Y_AXIS)
  const ordered: { axis: "x" | "y"; delta: number; at: number }[] = []
  if (alongX && alongY) {
    const horizontalFirst = Math.abs(alongX.delta) <= Math.abs(alongY.delta)
    ordered.push(horizontalFirst ? { axis: "x", ...alongX } : { axis: "y", ...alongY })
    ordered.push(horizontalFirst ? { axis: "y", ...alongY } : { axis: "x", ...alongX })
  } else if (alongX) {
    ordered.push({ axis: "x", ...alongX })
  } else if (alongY) {
    ordered.push({ axis: "y", ...alongY })
  }

  for (const candidate of ordered) {
    const rect = resizedRect(proposed, anchor, candidate.axis, candidate.delta)
    if (!rect || !resizeFits(rect, limits)) continue
    return { rect, guides: [resizeGuide(candidate.axis, candidate.at, rect, others)], anchor }
  }
  return { rect: proposed, guides: [], anchor }
}
